import $ from 'jquery';
import svgPanZoom from 'svg-pan-zoom';
import Hammer from 'hammerjs';

// File: js/native-svg-property-map.js
export default class SvgPropertyMap {
    constructor(containerId = 'property-map-container') {
        this.mapContainer = document.getElementById(containerId);
        if (!this.mapContainer) return;
        
        // Initialize state
        this.defaultViewBox = { x: 0, y: 0, w: 865, h: 589 };
        this.mobileViewBox = { x: 0, y: 0, w: 865, h: 589 };
        this.initialized = false;
        this.svgPanZoomInitialized = false;
        this.eventsAttached = false;
        this.mapAccordion = null;
        
        // Define initial state for mobile devices to match the previous CSS transform
        this.mobileInitialState = {
            zoom: 2.25, // Updated zoom level
            pan: { x: -540.21, y: -110.21 } // Updated pan position
        };
        
        // Initialize notice timeout
        this.noticeTimeout = null;
        
        // Skip the rest of initialization if no map container
        if (!this.mapContainer) return;
        
        // Find if map is inside an accordion
        this.mapAccordion = this.findParentAccordion();
        
        // Bind methods only after they're defined to avoid errors
        this.bindMethods();
        
        // Set up initial map elements
        this.setupMapElements();
        
        // For mobile maps in accordions, only set up event listeners
        if (this.isMobile() && this.mapAccordion) {
            // Just set up listeners, don't initialize yet
            this.setupAccordionListeners();
            // Initialize pins but not SVG-Pan-Zoom
            this.initializePins();
        } else {
            // For desktop or maps not in accordions, initialize fully
            this.initializeMap();
        }
        
        // Setup visibility detection for various scenarios
        this.setupVisibilityDetection();
    }
    
    // Initialize the map completely
    initializeMap() {
        try {
            if (!this.mapContainer) return;
            
            // Make sure dimensions are valid first
            this.setupMapElements();
            
            // Initialize pins if not already done
            if (!this.initialized) {
                this.initializePins();
            }
            
            // Check if container has valid dimensions before initializing SVG-Pan-Zoom
            if (this.mapContainer.offsetWidth > 0 && this.mapContainer.offsetHeight > 0) {
                // Only initialize SVG-Pan-Zoom if not already done
                if (!this.svgPanZoomInitialized) {
                    this.initSvgPanZoom();
                    this.svgPanZoomInitialized = true;
                }
                
                // Update pin size based on zoom level
                if (this.panZoom) {
                    try {
                        const zoom = this.isMobile() ? this.mobileInitialState.zoom : 1;
                        this.updatePinSize(zoom);
                    } catch (e) {
                        console.error("Error updating pin size:", e);
                    }
                }
                
                // Initialize map controls
                this.initializeMapControls();
                
                // Attach event handlers
                this.attachEvents();
                
                this.initialized = true;
            } else {
                console.log("Map container has invalid dimensions, not initializing SVG-Pan-Zoom");
            }
        } catch (error) {
            console.error("Error initializing map:", error);
        }
    }
    
    // Find if the map is inside an accordion
    findParentAccordion() {
        if (!this.mapContainer) return null;
        
        let parent = this.mapContainer.parentElement;
        while (parent) {
            // Check if this is an accordion content
            if (parent.classList.contains('c-accordion_content')) {
                const accordion = parent.closest('.c-accordion');
                return accordion;
            }
            parent = parent.parentElement;
        }
        return null;
    }
    
    // Special setup for accordion-contained maps
    setupAccordionListeners() {
        if (!this.mapAccordion) return;
        
        // Find the accordion button
        const accordionBtn = this.mapAccordion.querySelector('.c-accordion_btn');
        if (!accordionBtn) return;
        
        // Listen for accordion expand/collapse
        accordionBtn.addEventListener('click', () => {
            // After accordion animation completes
            setTimeout(() => {
                // Check if accordion is expanded
                const isExpanded = accordionBtn.getAttribute('aria-expanded') === 'true';
                
                if (isExpanded) {
                    // If expanded, ensure the map is visible and properly sized
                    if (this.mapContainer) {
                        this.mapContainer.style.display = 'block';
                        
                        // Ensure the container has height
                        if (this.mapContainer.offsetHeight < 300) {
                            this.mapContainer.style.height = '500px';
                        }
                        
                        // Make sure the SVG takes the full container size
                        if (this.svg) {
                            this.svg.style.width = '100%';
                            this.svg.style.height = '100%';
                        }
                        
                        // Must clean up any previous initialization
                        if (this.svgPanZoomInitialized) {
                            try {
                                if (this.panZoom) {
                                    this.panZoom.destroy();
                                    this.panZoom = null;
                                }
                                this.svgPanZoomInitialized = false;
                            } catch (e) {
                                console.error("Error cleaning up previous SVG-Pan-Zoom:", e);
                            }
                        }
                        
                        // Wait for a small delay to ensure dimensions are applied
                        setTimeout(() => {
                            // Now initialize the map with proper dimensions
                            this.initializeMap();
                        }, 50);
                    }
                }
            }, 400); // Delay to match accordion animation duration
        });
    }
    
    // Bind all methods to 'this' after they're defined
    bindMethods() {
        // Ensure these methods exist before binding
        if (typeof this.handleWheel === 'function') {
            this.handleWheel = this.handleWheel.bind(this);
        }
        if (typeof this.handleKeyDown === 'function') {
            this.handleKeyDown = this.handleKeyDown.bind(this);
        }
        if (typeof this.handleKeyUp === 'function') {
            this.handleKeyUp = this.handleKeyUp.bind(this);
        }
        if (typeof this.handleMouseDown === 'function') {
            this.handleMouseDown = this.handleMouseDown.bind(this);
        }
        if (typeof this.handleMouseMove === 'function') {
            this.handleMouseMove = this.handleMouseMove.bind(this);
        }
        if (typeof this.handleMouseUp === 'function') {
            this.handleMouseUp = this.handleMouseUp.bind(this);
        }
        
        // These should always exist
        this.setupMapElements = this.setupMapElements.bind(this);
        this.refreshMapDisplay = this.refreshMapDisplay.bind(this);
        this.initializeMap = this.initializeMap.bind(this);
    }

    // Helper method to consistently detect mobile devices
    isMobile() {
        return window.innerWidth < 768;
    }
    
    // Set up the SVG and wrapper elements
    setupMapElements() {
        if (!this.mapContainer) return;
        
        try {
            this.svgWrapper = this.mapContainer.querySelector('.svg-wrapper');
            this.svg = this.svgWrapper?.querySelector('#property-map-svg');
            
            if (!this.svgWrapper || !this.svg) return;
            
            // Ensure the SVG wrapper has position: relative to properly handle mouse events
            this.svgWrapper.style.position = 'relative';
            
            // Remove any existing transforms from CSS that might conflict with programmatic transforms
            const svgStyle = window.getComputedStyle(this.svg);
            if (svgStyle.transform && svgStyle.transform !== 'none') {
                // Remove the transform from the style to avoid conflicts
                this.svg.style.transform = 'none';
            }
            
            // For iOS specifically, need to prevent elastic scrolling, but allow pointer events
            this.mapContainer.style.overscrollBehavior = 'none';
            this.mapContainer.style.touchAction = 'manipulation'; // Allow taps but no double-tap zoom
            
            // Set appropriate viewbox for mobile vs desktop
            if (this.isMobile()) {
                this.svg.setAttribute('viewBox', `${this.mobileViewBox.x} ${this.mobileViewBox.y} ${this.mobileViewBox.w} ${this.mobileViewBox.h}`);
                // Set mobile-specific rendering properties
                this.svg.style.imageRendering = 'optimizeQuality';
                this.svg.setAttribute('shape-rendering', 'geometricPrecision');
            } else {
                this.svg.setAttribute('viewBox', `${this.defaultViewBox.x} ${this.defaultViewBox.y} ${this.defaultViewBox.w} ${this.defaultViewBox.h}`);
                // Set desktop-specific rendering properties
                this.svg.style.imageRendering = 'crisp-edges';
                this.svg.setAttribute('shape-rendering', 'crispEdges');
            }
            
            this.svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
            
            // Ensure SVG takes full container size
            this.svg.style.width = '100%';
            this.svg.style.height = '100%';
            
            // Make sure map container has a defined height on mobile
            if (this.isMobile() && (this.mapContainer.offsetHeight === 0 || this.mapContainer.offsetHeight < 300)) {
                // Only set height if not in a collapsed accordion
                if (!(this.mapAccordion && this.isMobile() && 
                      this.mapAccordion.querySelector('.c-accordion_btn')?.getAttribute('aria-expanded') === 'false')) {
                    this.mapContainer.style.height = '500px';
                }
            }
        } catch (error) {
            console.error("Error in setupMapElements:", error);
        }
    }
    
    // Set up detection of visibility changes for the map container
    setupVisibilityDetection() {
        try {
            // Also add event listeners for accordion clicks that might affect the map
            document.addEventListener('click', event => {
                // Check if click was on or inside an accordion button
                const accordionButton = event.target.closest('.c-accordion_btn');
                if (accordionButton) {
                    // Ignore our own map accordion - it has specific handlers
                    if (this.mapAccordion && accordionButton.closest('.c-accordion') === this.mapAccordion) {
                        return;
                    }
                    
                    // For other accordions, refresh after delay
                    setTimeout(() => this.refreshMapDisplay(), 400);
                }
            });
            
            // Track previous window width to detect horizontal resizes
            this.previousWindowWidth = window.innerWidth;
            
            // Listen for window resize events with debounce
            let resizeTimeout;
            window.addEventListener('resize', () => {
                // Clear previous timeout to debounce
                if (resizeTimeout) clearTimeout(resizeTimeout);
                
                // Get current window width
                const currentWindowWidth = window.innerWidth;
                
                // Only respond if the width changed significantly (>5px)
                // This ignores vertical resizes which don't change the width
                if (Math.abs(currentWindowWidth - this.previousWindowWidth) > 5) {
                    // Set new timeout
                    resizeTimeout = setTimeout(() => {
                        const wasMobile = this.previousWindowWidth < 768;
                        const isMobile = currentWindowWidth < 768;
                        
                        // Setup elements with new dimensions
                        this.setupMapElements();
                        
                        // If mobile state changed, need to reinitialize with correct settings
                        if (wasMobile !== isMobile) {
                            // If switching to desktop from mobile
                            if (!isMobile && this.mapAccordion) {
                                // For desktop, ensure the map is fully initialized
                                this.initializeMap();
                            } else if (this.svgPanZoomInitialized) {
                                this.refreshMapDisplay();
                            }
                        } else if (this.svgPanZoomInitialized && this.panZoom) {
                            // Same device type, just update pin sizes using current zoom
                            try {
                                if (isMobile) {
                                    // For mobile, use the mobile zoom level
                                    this.updatePinSize(this.mobileInitialState.zoom);
                                } else {
                                    // For desktop, use the current zoom
                                    const currentZoom = this.panZoom.getZoom();
                                    this.updatePinSize(currentZoom);
                                }
                            } catch (e) {
                                console.error("Error updating pins during resize:", e);
                            }
                        }
                        
                        // Update the previous width for next comparison
                        this.previousWindowWidth = currentWindowWidth;
                    }, 100); // 100ms debounce
                }
            });
            
            // For maps not in accordions on mobile, check again after a delay
            if (!this.mapAccordion || !this.isMobile()) {
                setTimeout(() => {
                    if (this.svgPanZoomInitialized) {
                        this.refreshMapDisplay();
                    } else {
                        this.initializeMap();
                    }
                }, 500);
            }
        } catch (error) {
            console.error("Error in setupVisibilityDetection:", error);
        }
    }
    
    // Method to refresh the map display when needed
    refreshMapDisplay() {
        if (!this.panZoom) return;
        
        // Force a refresh of the SVG pan zoom instance
        try {
            // Reset view for mobile devices
            if (this.isMobile()) {
                this.panZoom.zoom(this.mobileInitialState.zoom);
                this.panZoom.pan(this.mobileInitialState.pan);
                this.updatePinSize(this.mobileInitialState.zoom);
            } else {
                const currentZoom = this.panZoom.getZoom();
                this.panZoom.zoom(currentZoom);
                this.updatePinSize(currentZoom);
            }
        } catch (error) {
            console.error("Error refreshing map display:", error);
        }
    }

    init() {
        // Redirect to the new initializeMap method
        this.initializeMap();
    }
    
    // Attach all event handlers
    attachEvents() {
        if (this.eventsAttached) return;
        if (!this.mapContainer || !this.svgWrapper || !this.svg) return;
        
        try {
            // Ensure methods are bound before attaching
            this.bindMethods();
            
            // Add command key event listeners
            document.addEventListener('keydown', this.handleKeyDown);
            document.addEventListener('keyup', this.handleKeyUp);

            // Add mouse events to both SVG and wrapper
            this.mapContainer.addEventListener('mousedown', this.handleMouseDown);
            document.addEventListener('mousemove', this.handleMouseMove); // Track on document to catch drags outside container
            document.addEventListener('mouseup', this.handleMouseUp); // Track on document to catch releases outside container

            // Replace svg-pan-zoom's wheel handling with our custom handler
            this.svgWrapper.addEventListener('wheel', this.handleWheel, { passive: false });
            
            this.eventsAttached = true;
        } catch (error) {
            console.error("Error attaching events:", error);
        }
    }
    
    // Detach all event handlers
    detachEvents() {
        if (!this.eventsAttached) return;
        
        // Remove keyboard event listeners
        document.removeEventListener('keydown', this.handleKeyDown);
        document.removeEventListener('keyup', this.handleKeyUp);
        
        // Remove mouse event listeners
        if (this.mapContainer) {
            this.mapContainer.removeEventListener('mousedown', this.handleMouseDown);
        }
        document.removeEventListener('mousemove', this.handleMouseMove);
        document.removeEventListener('mouseup', this.handleMouseUp);
        
        // Remove wheel event listener
        if (this.svgWrapper) {
            this.svgWrapper.removeEventListener('wheel', this.handleWheel, { passive: false });
        }
        
        this.eventsAttached = false;
    }

    handleKeyDown(e) {
            // Ignore if focus is in the filter accordion area
            if (e.target.closest('.c-section_property_filters')) {
                return;
            }
            
            if (e.metaKey || e.ctrlKey) {
            // Update key state tracking
            this.isCommandPressed = e.metaKey;
            this.isControlPressed = e.ctrlKey;
            
            // Only enable mousewheel zoom when command key is pressed
            if (this.panZoom) {
                try {
                    this.panZoom.enableMouseWheelZoom();
                    
                    // Add visual indicator for zoom mode
                    document.body.style.cursor = 'grab';
                } catch (error) {
                    // Error handling without console logging
                }
                }
                // Stop Lenis scrolling when command key is pressed
            const lenis = this.getLenis();
            if (lenis) {
                try {
                    lenis.stop();
                } catch (error) {
                    // Error handling without console logging
                }
            }
        }
    }

    handleKeyUp(e) {
            // Ignore if focus is in the filter accordion area
            if (e.target.closest('.c-section_property_filters')) {
                return;
            }
            
            if (e.key === 'Meta' || e.key === 'Control') {
            // Update key state tracking
            this.isCommandPressed = false;
            this.isControlPressed = false;
            
            // Disable mousewheel zoom when command key is released
            if (this.panZoom) {
                try {
                    this.panZoom.disableMouseWheelZoom();
                    
                    // Reset cursor
                    document.body.style.cursor = '';
                } catch (error) {
                    // Error handling without console logging
                }
                }
                // Restart Lenis scrolling when command key is released
            const lenis = this.getLenis();
            if (lenis) {
                try {
                    lenis.start();
                } catch (error) {
                    // Error handling without console logging
                }
            }
        }
    }

    handleMouseDown(e) {
            // Ignore clicks on map controls and their buttons
            if (e.target.closest('.map-controls') || e.target.closest('.zoom-in') || e.target.closest('.zoom-out')) {
                e.stopPropagation();
                return;
            }
            
            // Also ignore clicks on filter accordion elements
            if (e.target.closest('.c-section_property_filters')) {
                return;
            }
            
        const isMobile = this.isMobile();
        const isCommandPressed = e.metaKey || e.ctrlKey;
        
        // Update key state tracking
        this.isCommandPressed = isCommandPressed;
        
        // Check if we're clicking on a pin or pin wrapper
        const isClickingPin = e.target.closest('.pin-wrapper') || e.target.closest('.property-pin');
        
        // Set cursor to grabbing during mouse down for panning
        document.body.style.cursor = 'grabbing';
        
                // Hide popup if visible
                const popup = document.getElementById('city-properties-popup');
                if (popup) popup.remove();
        
        // Let pin clicks be handled by their own event handlers
        if (isClickingPin) {
            return;
        }
        
        // Update visual indicator for active grabbing
        this.svgWrapper.classList.add('pan-enabled');
        
        // If the click is on the svg-wrapper and not directly on the SVG or its children,
        // generate a simulated event on the SVG element to ensure proper pan behavior
        if (e.target === this.svgWrapper && this.panZoom) {
            try {
                // Create a new mouse event that matches the original
                const simulatedEvent = new MouseEvent(e.type, {
                    bubbles: e.bubbles,
                    cancelable: e.cancelable,
                    clientX: e.clientX,
                    clientY: e.clientY,
                    button: e.button,
                    buttons: e.buttons
                });
                
                // Dispatch the event on the SVG element
                this.svg.dispatchEvent(simulatedEvent);
                
                // Prevent the original event to avoid duplicate handling
                e.preventDefault();
                e.stopPropagation();
            } catch (error) {
                // Error handling without console log
            }
        }
    }

    handleMouseMove(e) {
            // Ignore moves on map controls and their buttons
            if (e.target.closest('.map-controls') || e.target.closest('.zoom-in') || e.target.closest('.zoom-out')) {
                e.stopPropagation();
                return;
            }
            
            // Also ignore moves on filter accordion elements
            if (e.target.closest('.c-section_property_filters')) {
                return;
            }
            
        const isMobile = this.isMobile();
        const isCommandPressed = e.metaKey || e.ctrlKey;
        
        // Update key state tracking
        this.isCommandPressed = isCommandPressed;
        
        // Different handling based on whether mouse button is pressed (dragging)
        const isDragging = e.buttons === 1; // buttons === 1 means left mouse button is pressed
        
        // During active drag operations, set cursor to grabbing
        if (isDragging) {
            document.body.style.cursor = 'grabbing';
        } else if (isCommandPressed) {
            // Hovering with command key - show grab cursor for zoom capability
            document.body.style.cursor = 'grab';
        } else {
            // Basic hover - show default cursor
            document.body.style.cursor = '';
        }
        
        // If we're dragging and the event is on the svg-wrapper, forward it to the SVG
        if (isDragging && e.target === this.svgWrapper && this.panZoom) {
            try {
                // Create a new mouse event that matches the original
                const simulatedEvent = new MouseEvent(e.type, {
                    bubbles: e.bubbles,
                    cancelable: e.cancelable,
                    clientX: e.clientX,
                    clientY: e.clientY,
                    button: e.button,
                    buttons: e.buttons
                });
                
                // Dispatch the event on the SVG element
                this.svg.dispatchEvent(simulatedEvent);
                
                // Prevent the original event to avoid duplicate handling
                e.preventDefault();
                e.stopPropagation();
            } catch (error) {
                // Error handling without console log
            }
        }
    }

    handleMouseUp(e) {
        // After mouse up, restore cursor to default or grab if command key is still pressed
        if (this.isCommandPressed || this.isControlPressed) {
            document.body.style.cursor = 'grab';
        } else {
            document.body.style.cursor = '';
        }
        
        // Remove the pan-enabled class
        this.svgWrapper.classList.remove('pan-enabled');
        
        // If the event is on the svg-wrapper, forward it to the SVG
        if (e.target === this.svgWrapper && this.panZoom) {
            try {
                // Create a new mouse event that matches the original
                const simulatedEvent = new MouseEvent(e.type, {
                    bubbles: e.bubbles,
                    cancelable: e.cancelable,
                    clientX: e.clientX,
                    clientY: e.clientY,
                    button: e.button,
                    buttons: e.buttons
                });
                
                // Dispatch the event on the SVG element
                this.svg.dispatchEvent(simulatedEvent);
                
                // Prevent the original event to avoid duplicate handling
                e.preventDefault();
                e.stopPropagation();
            } catch (error) {
                // Error handling without console log
            }
        }
    }

    initSvgPanZoom() {
        const isMobile = this.isMobile();
        
        // Set high-quality SVG rendering
        if (isMobile) {
            this.svg.style.imageRendering = 'high-quality';
            this.svg.setAttribute('shape-rendering', 'geometricPrecision');
            this.svg.style.textRendering = 'optimizeLegibility';
        }
        
        // Disable the default touch actions on the SVG and wrapper to prevent browser handling
        this.svgWrapper.style.touchAction = 'none';
        this.svg.style.touchAction = 'none';
        
        // Add meta viewport tag to prevent page zooming on mobile
        const metaViewport = document.querySelector('meta[name="viewport"]');
        if (metaViewport) {
            metaViewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
        } else {
            const newMetaViewport = document.createElement('meta');
            newMetaViewport.setAttribute('name', 'viewport');
            newMetaViewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
            document.head.appendChild(newMetaViewport);
        }
        
        // Check if the SVG has valid dimensions before initializing
        const svgRect = this.svg.getBoundingClientRect();
        if (svgRect.width <= 0 || svgRect.height <= 0) {
            console.warn("Cannot initialize SVG-Pan-Zoom - SVG has zero dimensions");
            return false;
        }
        
        // Create custom events handler to integrate Hammer.js with svg-pan-zoom
        const customEventsHandler = {
            haltEventListeners: ['touchstart', 'touchend', 'touchmove', 'touchleave', 'touchcancel'],
            
            init: (options) => {
                // Initialize Hammer with proper input class
                const hammer = new Hammer(this.svgWrapper, {
                    inputClass: Hammer.SUPPORT_POINTER_EVENTS ? 
                        Hammer.PointerEventInput : Hammer.TouchInput
                });
                
                // Enable pinch gesture recognition
                hammer.get('pinch').set({ enable: true });
                
                // Enable pan gesture recognition with proper direction
                hammer.get('pan').set({ 
                    direction: Hammer.DIRECTION_ALL,
                    threshold: 0
                });
                
                // Enable tap recognizer with settings for doubletap
                const tap = new Hammer.Tap({ taps: 2 });
                hammer.add(tap);
                
                // Store initial zoom for calculations
                let initialScale = 1;
                let panned = { x: 0, y: 0 };
                
                // Track pin elements for tap detection
                const isPinElement = (target) => {
                    return target.closest('.pin-wrapper') || target.closest('.property-pin');
                };
                
                // Handle tap for pins
                hammer.on('tap', (ev) => {
                    const target = document.elementFromPoint(ev.center.x, ev.center.y);
                    if (isPinElement(target)) {
                        const pinElement = target.closest('.pin-wrapper') || target.closest('.property-pin');
                        const city = pinElement.getAttribute('data-city');
                        const count = pinElement.getAttribute('data-count');
                        const state = pinElement.getAttribute('data-state');
                        const citySlug = pinElement.getAttribute('data-slug');
                        
                        if (city && count && state) {
                            // Remove active class from all pins
                            const pins = this.mapContainer.querySelectorAll('.property-pin, .pin-wrapper');
                            pins.forEach(p => p.classList.remove('active'));
                            
                            // Add active class to tapped pin
                            if (pinElement.classList.contains('property-pin')) {
                                pinElement.classList.add('active');
                            } else {
                                const innerPin = pinElement.querySelector('.property-pin');
                                if (innerPin) innerPin.classList.add('active');
                            }
                            
                            this.showPropertyPopup(city, count, state, citySlug, pinElement);
                        }
                    }
                });
                
                // Handle double tap for reset
                hammer.on('doubletap', (ev) => {
                    const target = document.elementFromPoint(ev.center.x, ev.center.y);
                    if (!isPinElement(target)) {
                        // Double tap on empty map area - reset to initial state
                        this.resetToInitialState();
                    }
                });
                
                // Handle pan events
                hammer.on('panstart panmove', (ev) => {
                    const target = document.elementFromPoint(ev.center.x, ev.center.y);
                    if (isPinElement(target)) return;
                
                // Hide popup if visible
                const popup = document.getElementById('city-properties-popup');
                if (popup) popup.remove();
                
                    // On pan start, reset panned variables
                    if (ev.type === 'panstart') {
                        panned = { x: 0, y: 0 };
                    }
                    
                    // Pan only the difference
                    options.instance.panBy({
                        x: ev.deltaX - panned.x,
                        y: ev.deltaY - panned.y
                    });
                    
                    // Log current pan position
                    try {
                        const currentPan = options.instance.getPan();
                    } catch (error) {
                    }
                    
                    panned.x = ev.deltaX;
                    panned.y = ev.deltaY;
                });
                
                // Add momentum panning on pan end
                hammer.on('panend', (ev) => {
                    // Log the final pan position at the end of manual panning
                    try {
                        const panPosition = options.instance.getPan();
                    } catch (error) {
                    }
                    
                    this.lastPanDeltaX = 0;
                    this.lastPanDeltaY = 0;
                    
                    if (ev.velocityX === 0 && ev.velocityY === 0) return;
                    
                    // Calculate momentum based on velocity
                    const decayFactor = 0.95;
                    let velocity = { 
                        x: ev.velocityX * 15, // Scale by a factor to increase effect
                        y: ev.velocityY * 15
                    };
                    
                    let momentumCount = 0;
                    const applyMomentum = () => {
                        // Reduce velocity with each frame
                        velocity.x *= decayFactor;
                        velocity.y *= decayFactor;
                        
                        // Apply pan if there's meaningful movement
                        if (Math.abs(velocity.x) > 0.1 || Math.abs(velocity.y) > 0.1) {
                            options.instance.panBy({
                                x: velocity.x,
                                y: velocity.y
                            });
                            
                            // Log momentum panning periodically (every 10 frames)
                            momentumCount++;
                            if (momentumCount % 10 === 0) {
                                try {
                                    const currentPan = options.instance.getPan();
                                } catch (error) {
                                }
                            }
                            
                            requestAnimationFrame(applyMomentum);
            } else {
                            // Log final position after momentum
                            try {
                                const finalPan = options.instance.getPan();
                            } catch (error) {
                            }
                        }
                    };
                    
                    // Start momentum animation
                    requestAnimationFrame(applyMomentum);
                });
                
                // Handle pinch events
                hammer.on('pinchstart pinchmove', (ev) => {
                // Hide popup if visible
                const popup = document.getElementById('city-properties-popup');
                if (popup) popup.remove();
                    
                    // Stop Lenis scrolling during pinch
                    const lenis = this.getLenis();
                    if (lenis) {
                        try {
                            lenis.stop();
                        } catch (error) {
                        }
                    }
                    
                    // On pinch start, remember initial zoom
                    if (ev.type === 'pinchstart') {
                        initialScale = options.instance.getZoom();
                    }
                    
                    // Calculate new scale with limits
                    const maxZoom = 30;
                    const minZoom = 1;
                    const newScale = Math.max(Math.min(initialScale * ev.scale, maxZoom), minZoom);
                    
                    // Get center point for zooming relative to SVG
                    const svgRect = this.svg.getBoundingClientRect();
                    const center = {
                        x: ev.center.x - svgRect.left,
                        y: ev.center.y - svgRect.top
                    };
                    
                    // Apply zoom at specific point
                    options.instance.zoomAtPoint(newScale, center);
                    
                    // Update pin sizes for the new zoom level
                    this.updatePinSize(newScale);
                });
                
                // Handle pinch end
                hammer.on('pinchend', (ev) => {
                    // Force rerender for better quality
                    this.onZoomComplete();
                    
                    // Restart Lenis with a delay
                    setTimeout(() => {
                        const lenis = this.getLenis();
                        if (lenis) {
                            try {
                                lenis.start();
                            } catch (error) {
                            }
                        }
                    }, 200);
                });
                
                // Store hammer instance for cleanup
                this.hammer = hammer;
                
                // Prevent default touch actions to avoid conflicts
                this.svgWrapper.addEventListener('touchmove', (e) => {
                    e.preventDefault();
                }, { passive: false });
            },
            
            destroy: () => {
                // Clean up hammer instance
                if (this.hammer) {
                    this.hammer.destroy();
                }
            }
        };
        
        try {
            // Initialize svg-pan-zoom with the custom events handler
            this.panZoom = svgPanZoom(this.svg, {
                panEnabled: true,
                zoomEnabled: true,
                dblClickZoomEnabled: false,
                mouseWheelZoomEnabled: !isMobile, // Enable mousewheel only on desktop with command key
                preventMouseEventsDefault: false,
                controlIconsEnabled: false,
                zoomScaleSensitivity: 0.2,
                minZoom: 1,
                maxZoom: 50,
                fit: false,
                center: false,
                customEventsHandler: isMobile ? customEventsHandler : null, // Use custom handler only on mobile
                beforePan: (oldPan, newPan) => {
                    // Apply grabbing cursor during pan
                    if (!isMobile) {
                        document.body.style.cursor = 'grabbing';
                    }
                    return true; // Allow pan
                },
                beforeZoom: () => {
            // Stop Lenis scrolling during zoom
            if (this.lenis) {
                this.lenis.stop();
            }
                    return true; // Allow zoom
                },
                onZoom: (newZoom) => {
                    // Safety check for invalid zoom
                    if (isNaN(newZoom) || !isFinite(newZoom) || newZoom <= 0) {
                        newZoom = 1;
            }
            
            // Update pin sizes based on current zoom level
                    this.updatePinSize(newZoom);
            
            // Apply high-quality rendering during zooming on mobile
            if (isMobile) {
                // During active zooming, prioritize speed for smoother experience
                this.svg.setAttribute('shape-rendering', 'optimizeSpeed');
                this.svg.style.imageRendering = 'auto';
            }
                },
                onPan: () => {
                    // Hide popup if visible during pan
                    const popup = document.getElementById('city-properties-popup');
                    if (popup) popup.remove();
                    
                    // Make sure cursor is set to grabbing during active panning
                    if (this.isCommandPressed || this.isControlPressed) {
                        document.body.style.cursor = 'grabbing';
                    }
                    
                    // Log current pan position
                    try {
                        const currentPan = this.panZoom.getPan();
                    } catch (error) {
                    }
                }
            });
            
            // Set initial state for mobile devices - now with validity checks
            if (isMobile) {
                try {
                    this.panZoom.zoom(this.mobileInitialState.zoom);
                    this.panZoom.pan(this.mobileInitialState.pan);
                    
                    // Update pins for the new zoom level
                    this.updatePinSize(this.mobileInitialState.zoom);
                } catch (error) {
                    console.error("Error applying initial mobile state:", error);
                    return false;
                }
            } else {
                // Only disable mousewheel zoom by default on desktop (will be enabled when command key is pressed)
                // But keep panning enabled
                this.panZoom.disableMouseWheelZoom();
                this.svgWrapper.classList.add('pan-enabled');
            }
            
            // Setup zoom end detection
            let zoomEndTimeout;
            this.panZoom.setOnZoom((newZoom) => {
                // Safety check for invalid zoom
                if (isNaN(newZoom) || !isFinite(newZoom)) {
                    newZoom = 1;
                }
                
                // Update pin sizes based on current zoom level
                this.updatePinSize(newZoom);
                
                // Clear any existing timeout
                if (zoomEndTimeout) {
                    clearTimeout(zoomEndTimeout);
                }
                
                // Set a new timeout for zoom end detection
                zoomEndTimeout = setTimeout(() => {
                    this.onZoomComplete();
                    // Restart Lenis scrolling after zoom completes
                    const lenis = this.getLenis();
                    if (lenis) {
                        try {
                            lenis.start();
                        } catch (error) {
                        }
                    }
                    
                    // Restore cursor after zoom if command is still pressed
                    if (!isMobile && (this.isCommandPressed || this.isControlPressed)) {
                        document.body.style.cursor = 'grab';
                    } else {
                        document.body.style.cursor = '';
                    }
                }, 200);
            });
            
            // Successfully initialized
            return true;
        } catch (error) {
            console.error("Error initializing svg-pan-zoom:", error);
            return false;
        }
    }

    initializePins() {
        // Find all pins in the map
        const pins = this.mapContainer.querySelectorAll('.property-pin, .pin-wrapper');
        
        // Set up click events for each pin
        pins.forEach(pin => {
            const city = pin.getAttribute('data-city');
            const count = pin.getAttribute('data-count');
            const state = pin.getAttribute('data-state');
            let citySlug = pin.getAttribute('data-city-slug');
            
            // Fix for "null" string value - convert to actual null
            if (citySlug === "null") {
                citySlug = null;
            }
            
            // Add hover listeners to trigger rescale and reordering
            if (pin.classList.contains('property-pin')) {
            pin.addEventListener('mouseenter', () => {
                this.updatePinSize();
                // Move the pin wrapper to the end of its container
                const pinWrapper = pin.closest('.pin-wrapper');
                const pinsContainer = document.getElementById('property-pins');
                if (pinWrapper && pinsContainer) {
                    pinsContainer.appendChild(pinWrapper);
                }
            });
            
            pin.addEventListener('mouseleave', () => this.updatePinSize());
            }
            
            if (city && count && state) {
                // Mouse click event
            pin.addEventListener('click', (e) => {
                    e.preventDefault();
                e.stopPropagation();
                
                // Remove active class from all pins
                pins.forEach(p => p.classList.remove('active'));
                // Add active class to clicked pin
                    if (pin.classList.contains('property-pin')) {
                pin.classList.add('active');
                    } else {
                        const innerPin = pin.querySelector('.property-pin');
                        if (innerPin) innerPin.classList.add('active');
                    }
                    
                    this.showPropertyPopup(city, count, state, citySlug, pin);
                });
                
                // Additional touch event for mobile
                pin.addEventListener('touchend', (e) => {
                    // Only handle single taps with no movement
                    if (!this.hasTouchMoved && e.changedTouches.length === 1) {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        // Remove active class from all pins
                        pins.forEach(p => p.classList.remove('active'));
                        // Add active class to tapped pin
                        if (pin.classList.contains('property-pin')) {
                            pin.classList.add('active');
                        } else {
                            const innerPin = pin.querySelector('.property-pin');
                            if (innerPin) innerPin.classList.add('active');
                        }
                        
                    this.showPropertyPopup(city, count, state, citySlug, pin);
                }
            });
            }
        });
        
        // Ensure initial pin sizing is correct
        this.updatePinSize();
    }

    updatePinSize(zoom = null) {
        // Only get zoom from svg-pan-zoom if not provided
        if (zoom === null && this.panZoom) {
            try {
                zoom = this.panZoom.getZoom();
            } catch (error) {
                // Error handling without console logging
                zoom = 1; // Fallback to default zoom
            }
        }
        
        // Default to 1 if we still don't have a zoom value or if zoom is invalid
        if (zoom === null || isNaN(zoom) || !isFinite(zoom)) {
            zoom = 1;
        }
        
        const pins = this.mapContainer.querySelectorAll('.property-pin');
        // Use a fixed base scale with a slight adjustment for mobile vs desktop
        const baseScale = this.isMobile() ? 0.9 : 0.8; // Slightly larger on mobile but not too large
        
        // Zoom adjustment uses slightly different formulas based on device type
        let zoomAdjustment;
        try {
            if (this.isMobile()) {
                // Less aggressive formula for mobile devices
                zoomAdjustment = Math.min(1, 1 / Math.pow(Math.max(zoom, 0.5), 0.6));
            } else {
                // Original formula for desktop
                zoomAdjustment = Math.min(1, 1 / Math.pow(Math.max(zoom, 0.1), 0.75));
            }
            
            // Safety check for NaN or infinite values
            if (isNaN(zoomAdjustment) || !isFinite(zoomAdjustment)) {
                zoomAdjustment = 1;
            }
        } catch (error) {
            // Error handling without console logging
            zoomAdjustment = 1; // Fallback
        }
        
        const scale = baseScale * zoomAdjustment;
        
        pins.forEach(pin => {
            const path = pin.querySelector('.pin-shape');
            const circle = pin.querySelector('.pin-dot');
            
            if (path && circle) {
                try {
                    const cx = parseFloat(circle.getAttribute('cx') || 0);
                    const cy = parseFloat(circle.getAttribute('cy') || 0);
                    // Get the bottom point of the pin (12 units below the dot)
                    const bottomY = cy + 12;
                    
                    // Check if pin is currently being hovered
                    const isHovered = pin.matches(':hover');
                    // Apply a 20% increase to the current scale on hover
                    const hoverScale = isHovered ? scale * 1.2 : scale;
                    
                    // Validate values before setting transform
                    if (isNaN(cx) || isNaN(cy) || isNaN(bottomY) || isNaN(hoverScale)) {
                        return; // Skip this pin
                    }
                    
                    // Transform from the bottom point
                    pin.setAttribute('transform', 
                        `translate(${cx} ${bottomY}) scale(${hoverScale}) translate(${-cx} ${-bottomY})`
                    );
                } catch (error) {
                    console.error("Error updating pin size:", error);
                }
            }
        });
    }

    showPropertyPopup(city, count, state, citySlug, pinElement) {
        // Remove any existing popup and deactivate any active pins
        const existingPopup = document.getElementById('city-properties-popup');
        if (existingPopup) {
            existingPopup.remove();
            document.querySelectorAll('.property-pin').forEach(pin => {
                if (pin !== pinElement) {
                    pin.classList.remove('active');
                }
            });
        }

        const propertyText = count === 1 ? 'property' : 'properties';
        
        // Get state abbreviation from data attribute
        const stateAbbr = pinElement.getAttribute('data-state-abbr');
        
        // Create popup element
        const popup = document.createElement('div');
        popup.id = 'city-properties-popup';
        popup.className = 'city-properties-popup';
        
        // Create header
        const header = document.createElement('div');
        header.className = 'popup-header';
        header.innerHTML = `<h3>${city}, ${stateAbbr}</h3>`;

        const divider = document.createElement('span');
        divider.className = 'e-accent_divider';
        
        // Handle the case where citySlug is null, using city name as fallback
        // Convert to lowercase and replace spaces with hyphens to create a slug if needed
        const slug = citySlug || city.toLowerCase().replace(/\s+/g, '-');
        
        // Create content
        const content = document.createElement('div');
        content.className = 'popup-content';
        content.innerHTML = `
            <div class="property-info">
                <a href="/properties?cities=${slug}&states=${state}" id="property-popup-btn" class="btn btn-primary btn-sm" data-city="${city}" data-state="${state}" data-state-abbr="${stateAbbr}" data-city-slug="${slug}">Show ${count} ${propertyText}</a>
            </div>
        `;
        
        // Add header and content to popup
        popup.appendChild(header);
        popup.appendChild(divider);
        popup.appendChild(content);
        
        // Check if we're on mobile
        const isMobile = this.isMobile();
        
        if (isMobile) {
            // On mobile, append to container and position at bottom
            this.mapContainer.appendChild(popup);
            popup.style.position = 'absolute';
            popup.style.bottom = '32px';
            popup.style.left = '32px';
            popup.style.right = '32px';
            popup.style.width = 'calc(100% - 64px)';
            popup.style.zIndex = '1000';
        } else {
            // Desktop positioning logic
            document.body.appendChild(popup);
            
            // Get necessary measurements
            const pinRect = pinElement.getBoundingClientRect();
            const popupRect = popup.getBoundingClientRect();
            const containerRect = this.mapContainer.getBoundingClientRect();
            
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
            
            // Calculate if popup would exceed container boundaries
            const wouldExceedRight = (pinRect.left + popupRect.width) > containerRect.right;
            const wouldExceedBottom = (pinRect.bottom + popupRect.height + 10) > containerRect.bottom;
            
            // Calculate final position
            let left, top;
            
            // Handle horizontal positioning
            if (wouldExceedRight) {
                left = pinRect.right - popupRect.width + scrollLeft;
            } else {
                left = pinRect.left + scrollLeft;
            }
            
            // Handle vertical positioning
            if (wouldExceedBottom) {
                top = pinRect.top - popupRect.height - 10 + scrollTop;
            } else {
                top = pinRect.bottom + scrollTop + 10;
            }
            
            // Ensure popup doesn't go beyond left edge
            left = Math.max(containerRect.left + scrollLeft, left);
            
            // Apply final position
            popup.style.left = `${left}px`;
            popup.style.top = `${top}px`;
        }
        
        // Get reference to the button directly
        const popupBtn = popup.querySelector('#property-popup-btn');
        
        if (popupBtn) {
            // Add touchstart and click handlers for better mobile support
            ['touchstart', 'mousedown'].forEach(eventType => {
                popupBtn.addEventListener(eventType, (e) => {
                    // Prevent the event from bubbling up to the document click handler
                    e.stopPropagation();
                });
            });
            
            // Handle actual click separately
            popupBtn.addEventListener('click', (e) => {
                // Stop propagation to prevent other handlers from interfering
                e.stopPropagation();
                e.preventDefault();
                
                // Check if we're already on the properties page
                const isOnPortfolioPage = window.location.pathname.includes('/properties');
                
                if (!isOnPortfolioPage) {
                    // Navigate to portfolio page with query variables
                    const queryParams = new URLSearchParams({
                        cities: slug,
                        states: state
                    });
                    window.location.href = `/properties?${queryParams.toString()}`;
                } else {
                    // If already on portfolio page, trigger the filter event
                    if (window.jQuery) {
                        window.jQuery(document).trigger('mapPinFilter', {
                            city: slug,
                            state: state
                        });
                    }

                    // Scroll to property list
                    const propertyList = document.getElementById('property-list');
                    if (propertyList) {
                        propertyList.scrollIntoView({ behavior: 'smooth' });
                    }
                }
                
                // Clean up popup and event listeners
                popup.remove();
                pinElement.classList.remove('active');
                document.removeEventListener('click', closePopupHandler);
            });
        }
        
        // Close popup when clicking outside
        function closePopupHandler(event) {
            // If the click target is the button or inside the button, don't close the popup
            const isClickOnButton = event.target === popupBtn || popupBtn.contains(event.target);
            
            if (isClickOnButton) {
                return;
            }
            
            // Don't close if clicking elsewhere on the popup or on the pin
            if (popup.contains(event.target) || pinElement.contains(event.target)) {
                return;
            }
            
            // Close popup and remove event listener
                popup.remove();
                pinElement.classList.remove('active');
            document.removeEventListener('click', closePopupHandler);
            }
        
        // Add event listener for closing popup
        document.addEventListener('click', closePopupHandler);
    }

    showMapNotice() {
        const noticeContainer = this.mapContainer.querySelector('.map-notice-container');
        if (!noticeContainer) return;

        // Clear any existing timeout
        if (this.noticeTimeout) {
            clearTimeout(this.noticeTimeout);
        }

        // Add active class to show the notice
        noticeContainer.classList.add('active');

        // Set timeout to remove the active class
        this.noticeTimeout = setTimeout(() => {
            noticeContainer.classList.remove('active');
        }, 2000); // 2 seconds
    }

    // Update destroy method to clean up svg-pan-zoom
    destroy() {
        // Clean up SVG pan-zoom instance
        if (this.panZoom) {
            this.panZoom.destroy();
            this.panZoom = null;
        }
        
        // Clean up observers
        if (this.observer) {
            this.observer.disconnect();
        }
        
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        
        // Detach event listeners
        this.detachEvents();
        
        // Remove specific event listeners
        if (this.zoomInButton) {
            this.zoomInButton.removeEventListener('click', this.zoomInHandler);
        }
        
        if (this.zoomOutButton) {
            this.zoomOutButton.removeEventListener('click', this.zoomOutHandler);
        }
        
        if (this.resetButton) {
            this.resetButton.removeEventListener('click', this.resetHandler);
        }
        
        // Remove double tap/click handler
        if (this.svgWrapper) {
            this.svgWrapper.removeEventListener('dblclick', this.handleDoubleClick);
        }
        
        // Remove touch event listeners
        if (this.svg) {
            this.svg.removeEventListener('touchstart', this.handleTouchStart);
            this.svg.removeEventListener('touchmove', this.handleTouchMove);
            this.svg.removeEventListener('touchend', this.handleTouchEnd);
        }
        
        // Clear the popup if it's visible
        this.hidePopup();
        
        this.initialized = false;
        this.eventsAttached = false;
    }

    initializeMapControls() {
        // Get existing map controls container
        const mapControls = this.mapContainer.querySelector('.map-controls');
        if (!mapControls) {
            // Container not found, silently return
            return;
        }
        
        // Find existing zoom buttons
        const zoomInBtn = mapControls.querySelector('.zoom-in');
        const zoomOutBtn = mapControls.querySelector('.zoom-out');
        
        // Add event listeners for zoom buttons with improved handling
        if (zoomInBtn) {
            zoomInBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (this.panZoom) {
                    try {
                        const currentZoom = this.panZoom.getZoom();
                        const zoomFactor = 1.25; // 25% zoom in
                        
                        // Get center of SVG viewport as zoom target
                        const svgRect = this.svg.getBoundingClientRect();
                        const center = {
                            x: svgRect.width / 2,
                            y: svgRect.height / 2
                        };
                        
                        // Apply zoom at center
                        this.panZoom.zoomAtPoint(currentZoom * zoomFactor, center);
                    } catch (error) {
                        // Silently handle errors
                    }
                }
            });
        }
        
        if (zoomOutBtn) {
            zoomOutBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (this.panZoom) {
                    try {
                        const currentZoom = this.panZoom.getZoom();
                        const zoomFactor = 0.8; // 20% zoom out
                        
                        // Get center of SVG viewport as zoom target
                        const svgRect = this.svg.getBoundingClientRect();
                        const center = {
                            x: svgRect.width / 2,
                            y: svgRect.height / 2
                        };
                        
                        // Apply zoom at center
                        this.panZoom.zoomAtPoint(currentZoom * zoomFactor, center);
                    } catch (error) {
                        // Silently handle errors
                    }
                }
            });
        }
        
        // Add double-click handler on map to reset view as alternative to a reset button
        this.svgWrapper.addEventListener('dblclick', (e) => {
            // Only trigger if not clicking on a pin
            const target = e.target;
            const isPinElement = target.closest('.pin-wrapper') || target.closest('.property-pin');
            
            if (!isPinElement) {
                e.preventDefault();
                e.stopPropagation();
                this.resetToInitialState();
            }
        });
    }

    onZoomComplete() {
        if (this.isMobile()) {
            // Improve rendering quality after zoom on mobile
            this.svg.setAttribute('shape-rendering', 'geometricPrecision');
            this.svg.style.imageRendering = 'high-quality';
        }
        
        if (this.panZoom) {
            try {
                const zoom = this.panZoom.getZoom();
                this.updatePinSize(zoom);
            } catch (error) {
                // Silent error handling
            }
        }
    }

    // Track command/control key state
    isCommandPressed = false;
    isControlPressed = false;

    // Helper method to safely access Lenis instance
    getLenis() {
        // Check if window.app exists
        if (!window.app) {
            return null;
        }
        
        // Check if lenis is available
        if (!window.app.lenis) {
            return null;
        }
        
        // Verify it has a stop method
        if (typeof window.app.lenis.stop !== 'function' || 
            typeof window.app.lenis.start !== 'function') {
            return null;
        }
        
        return window.app.lenis;
    }
    
    // Update handleWheel method to use the getLenis helper
    handleWheel(event) {
        // Ignore wheel events from filter accordion
        if (event.target.closest('.c-section_property_filters')) {
            return;
        }
        
        const isMobile = this.isMobile();
        const hasModifier = event.metaKey || event.ctrlKey;
        
        // Only handle wheel with command/control key (or on mobile)
        if (!isMobile && !hasModifier) {
            this.showMapNotice();
            return;
        }
        
        // Prevent default browser behavior
        event.preventDefault();
        event.stopPropagation();
        
        // Hide popup if visible
        const popup = document.getElementById('city-properties-popup');
        if (popup) popup.remove();
        
        // Stop Lenis scrolling during wheel zoom
        const lenis = this.getLenis();
        if (lenis) {
            try {
                lenis.stop();
            } catch (error) {
                // Error handling without console logging
            }
        }
        
        // Don't proceed if no panZoom instance
        if (!this.panZoom) return;
        
        try {
            // Get current zoom level with validation
            let currentZoom;
            try {
                currentZoom = this.panZoom.getZoom();
                if (isNaN(currentZoom) || !isFinite(currentZoom) || currentZoom <= 0) {
                    currentZoom = 1;
                    // Try to reset zoom to 1
                    try {
                        const sizes = this.panZoom.getSizes();
                        const centerPoint = {
                            x: sizes.width / 2,
                            y: sizes.height / 2
                        };
                        this.panZoom.zoom(1);
                    } catch (resetError) {
                        // Error handling without console logging
                    }
                }
            } catch (error) {
                // Error handling without console logging
                currentZoom = 1; // Default fallback
            }
            
            // Calculate zoom direction and factor with small increments
            const delta = event.deltaY || event.detail || event.wheelDelta;
            const zoomFactor = delta > 0 ? 0.95 : 1.05; // Smaller increments (0.95/1.05 instead of 0.9/1.1)
            
            // Calculate new zoom level with limits and validation
            let newZoom;
            try {
                const maxZoom = this.panZoom.getSizes().maxZoom || 30;
                const minZoom = this.panZoom.getSizes().minZoom || 1;
                newZoom = Math.max(
                    Math.min(currentZoom * zoomFactor, maxZoom),
                    minZoom
                );
                
                // Safety check for valid zoom
                if (isNaN(newZoom) || !isFinite(newZoom) || newZoom <= 0) {
                    newZoom = currentZoom; // Keep current zoom instead
                }
            } catch (error) {
                // Error handling without console logging
                return; // Exit early on error
            }
            
            // Get mouse position relative to SVG with validation
            try {
                const svgRect = this.svg.getBoundingClientRect();
                const point = {
                    x: event.clientX - svgRect.left,
                    y: event.clientY - svgRect.top
                };
                
                // Validate point values
                if (isNaN(point.x) || isNaN(point.y)) {
                    // Use center point instead
                    const sizes = this.panZoom.getSizes();
                    point.x = sizes.width / 2;
                    point.y = sizes.height / 2;
                }
                
                // Apply zoom at specific point
                this.panZoom.zoomAtPoint(newZoom, point);
            } catch (error) {
                // Error handling without console logging
                // Try fallback to simpler zoom
                try {
                    this.panZoom.zoom(newZoom);
                } catch (fallbackError) {
                    // Error handling without console logging
                }
            }
            
            // Update pins with the new zoom level
            this.updatePinSize(newZoom);
            
            // Set timeout to restart Lenis
            setTimeout(() => {
                const lenis = this.getLenis();
                if (lenis) {
                    try {
                        lenis.start();
                    } catch (error) {
                        // Error handling without console logging
                    }
                }
            }, 200);
        } catch (error) {
            // Error handling without console logging
        }
    }

    // Add a method to reset the map to initial state
    resetToInitialState() {
        const isMobile = this.isMobile();
        
        try {
            if (isMobile && this.panZoom) {
                // For mobile, reset to the initial zoomed/panned state
                
                // Apply initial zoom and pan
                this.panZoom.zoom(this.mobileInitialState.zoom);
                this.panZoom.pan(this.mobileInitialState.pan);
                
                // Update pins for the new zoom level
                this.updatePinSize(this.mobileInitialState.zoom);
            } else if (this.panZoom) {
                // For desktop, reset to default view (zoom 1, centered)
                this.panZoom.resetZoom();
                this.panZoom.center();
                
                // Update pins for the new zoom level
                this.updatePinSize(1);
            }
        } catch (error) {
            // Error handling without console logging
        }
    }

    simulateClickOnSVG(x, y) {
        try {
            const svg = this.svg;
            const pt = svg.createSVGPoint();
            pt.x = x;
            pt.y = y;
            
            // Convert to SVG coordinates
            const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
            
            // Create and dispatch event
            const event = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window,
                detail: 1,
                screenX: x,
                screenY: y,
                clientX: x, 
                clientY: y
            });
            
            // Get element at that point and dispatch event
            const element = document.elementFromPoint(x, y);
            if (element) {
                element.dispatchEvent(event);
            }
        } catch (error) {
            // Error handling without console log
        }
    }

    hidePopup() {
        const popup = document.getElementById('city-properties-popup');
        if (popup) {
            popup.remove();
        }
    }
}